import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  listenToFriendSession,
  acceptFriendSession,
  declineFriendSession,
  endFriendSession,
  updateSessionPresence,
  updateSessionStudyMaterial,
  saveWordToSession,
  removeWordFromSession,
  getCumulativeFriendWords,
  sendSessionInviteNotification,
  FriendSession,
  SessionSavedItem,
  SessionAssignee,
} from '../services/friendSessionService';
import { useAuth } from '../contexts/AuthContext';
import NavbarLanding from '../components/NavbarLanding';
import Navigation from '../components/Navigation';
import { ClickableText } from '../components/news/ClickableText';
import { MaterialSelectorModal } from '../components/session/MaterialSelectorModal';
import { SessionTrainingModal } from '../components/session/SessionTrainingModal';
import { FinishSessionModal } from '../components/session/FinishSessionModal';
import { GameSelectionModal } from '../components/session/games/GameSelectionModal';
import { SessionGameArena } from '../components/session/games/SessionGameArena';
import { startSessionGame } from '../services/sessionGameService';
import { SessionGameType, SessionGameContentSource } from '../types/session-games';
import {
  Users,
  Sparkles,
  BookOpen,
  Volume2,
  Trash2,
  Plus,
  Play,
  Share2,
  LogOut,
  Check,
  CheckCircle2,
  Clock,
  ArrowLeft,
  Loader2,
  Copy,
  History,
  FileText,
  Bookmark,
  Layers,
  HelpCircle,
  Gamepad2,
  Bell,
  X,
  Compass,
  PenTool,
  ArrowRight,
  Newspaper,
} from 'lucide-react';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';

export default function FriendSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState<FriendSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [materialModalTab, setMaterialModalTab] = useState<'news' | 'theme' | 'custom'>('custom');
  const [isTrainingOpen, setIsTrainingOpen] = useState(false);
  const [isGameSelectorOpen, setIsGameSelectorOpen] = useState(false);
  const [activeMobileTab, setActiveMobileTab] = useState<'reader' | 'words'>('reader');
  const [wordFilter, setWordFilter] = useState<'all' | 'me' | 'friend' | 'both'>('all');
  const [showHistory, setShowHistory] = useState(false);
  const [cumulativeWords, setCumulativeWords] = useState<SessionSavedItem[]>([]);
  const [manualWordInput, setManualWordInput] = useState('');
  const [manualType, setManualType] = useState<'word' | 'expression'>('word');
  const [manualAssignee, setManualAssignee] = useState<SessionAssignee>('both');
  const [isAddingManual, setIsAddingManual] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [isPingingPartner, setIsPingingPartner] = useState(false);
  const [pingSent, setPingSent] = useState(false);

  const { speak } = useSpeechSynthesis({ lang: 'en-US' });

  // Listen to session data in RTDB
  useEffect(() => {
    if (!sessionId) return;

    setLoading(true);
    const unsubscribe = listenToFriendSession(sessionId, (data) => {
      setSession(data);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [sessionId]);

  // Presence heartbeat
  useEffect(() => {
    if (!sessionId || !currentUser?.uid || !session) return;

    updateSessionPresence(sessionId, currentUser.uid, true);

    const interval = setInterval(() => {
      updateSessionPresence(sessionId, currentUser.uid, true);
    }, 20000);

    return () => {
      clearInterval(interval);
      updateSessionPresence(sessionId, currentUser.uid, false);
    };
  }, [sessionId, currentUser?.uid, session?.status]);

  // Fetch cumulative words when session loads or when participants change
  useEffect(() => {
    if (!session?.hostId || !session?.friendId) return;

    getCumulativeFriendWords(session.hostId, session.friendId).then((words) => {
      setCumulativeWords(words);
    });
  }, [session?.hostId, session?.friendId]);

  if (loading) {
    return (
      <>
        <NavbarLanding isLanding={false} />
        <Navigation />
        <div className="min-h-screen flex items-center justify-center bg-gray-50 md:ml-64">
          <div className="text-center p-8">
            <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-700">Connecting to Learn Session...</p>
          </div>
        </div>
      </>
    );
  }

  if (!session) {
    return (
      <>
        <NavbarLanding isLanding={false} />
        <Navigation />
        <div className="min-h-screen flex items-center justify-center bg-gray-50 md:ml-64 p-4">
          <div className="text-center max-w-md bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-gray-900 mb-1">Session Not Found</h3>
            <p className="text-sm text-gray-500 mb-6">
              This study session may have expired or was removed.
            </p>
            <button
              onClick={() => navigate('/social')}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
            >
              Back to Friends
            </button>
          </div>
        </div>
      </>
    );
  }

  const isHost = currentUser?.uid === session.hostId;
  const partnerId = isHost ? session.friendId : session.hostId;
  const partnerName = isHost ? session.friendName : session.hostName;
  const partnerPhoto = isHost ? session.friendPhoto : session.hostPhoto;
  const partnerParticipant = session.participants?.[partnerId];
  const isPartnerOnline = partnerParticipant?.isOnline || false;

  const savedList = (Object.values(session.savedItems || {}) as SessionSavedItem[]).sort(
    (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
  );
  const displayItems = showHistory && cumulativeWords.length > 0 ? cumulativeWords : savedList;

  const filteredItems = displayItems.filter((item) => {
    if (wordFilter === 'all') return true;
    if (wordFilter === 'both') return item.assignedTo === 'both';
    if (wordFilter === 'me') {
      return item.assignedUserId === currentUser?.uid || item.assignedTo === 'both';
    }
    if (wordFilter === 'friend') {
      return item.assignedUserId === partnerId;
    }
    return true;
  });

  const handleCopyLink = () => {
    const url = `https://lingualearn.web.app/session/${sessionId}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handlePingPartner = async () => {
    if (!sessionId || !currentUser || !partnerId) return;
    setIsPingingPartner(true);
    try {
      await sendSessionInviteNotification(
        sessionId,
        {
          id: currentUser.uid,
          name: currentUser.displayName || currentUser.email?.split('@')[0] || 'Host',
          photo: currentUser.photoURL || undefined
        },
        partnerId
      );
      setPingSent(true);
      setTimeout(() => setPingSent(false), 3000);
    } catch (err) {
      console.error('Failed to ping partner:', err);
    } finally {
      setIsPingingPartner(false);
    }
  };

  const handleAcceptInvite = async () => {
    if (!currentUser) return;
    try {
      await acceptFriendSession(session.id, {
        id: currentUser.uid,
        name: currentUser.displayName || currentUser.email?.split('@')[0] || 'Friend',
        photo: currentUser.photoURL || undefined,
      });
    } catch (err) {
      console.error('Error accepting session:', err);
    }
  };

  const handleDeclineInvite = async () => {
    try {
      await declineFriendSession(session.id);
      navigate('/social');
    } catch (err) {
      console.error('Error declining session:', err);
    }
  };

  const handleEndSession = () => {
    setShowFinishModal(true);
  };

  const handleConfirmEndSession = async () => {
    if (!session) return;
    try {
      await endFriendSession(session.id);
      setShowFinishModal(false);
      navigate('/social');
    } catch (err) {
      console.error('Error ending session:', err);
    }
  };

  const handleSaveWordFromPopover = async (data: {
    word: string;
    isExpression: boolean;
    assignedTo: SessionAssignee;
    details?: any;
  }) => {
    if (!currentUser || !sessionId) return;

    let assignedUserId: string | 'both' = 'both';
    let assignedUserName = 'Both';

    if (data.assignedTo === 'me') {
      assignedUserId = currentUser.uid;
      assignedUserName = currentUser.displayName || 'You';
    } else if (data.assignedTo === 'friend') {
      assignedUserId = partnerId;
      assignedUserName = partnerName;
    }

    await saveWordToSession(
      sessionId,
      {
        word: data.word,
        type: data.isExpression ? 'expression' : 'word',
        definition: data.details?.definition,
        translation: data.details?.translation,
        exampleSentence: data.details?.examples?.[0],
        exampleTranslation: data.details?.contextTranslation,
        literalMeaning: data.details?.literalMeaning,
        assignedTo: data.assignedTo,
        assignedUserId,
        assignedUserName,
        addedByUserId: currentUser.uid,
        addedByUserName: currentUser.displayName || 'Friend',
      },
      {
        hostId: session.hostId,
        hostName: session.hostName,
        friendId: session.friendId,
        friendName: session.friendName,
      }
    );
  };

  const handleAddManualWord = async () => {
    if (!manualWordInput.trim() || !currentUser || !sessionId) return;

    setIsAddingManual(true);
    let assignedUserId: string | 'both' = 'both';
    let assignedUserName = 'Both';

    if (manualAssignee === 'me') {
      assignedUserId = currentUser.uid;
      assignedUserName = currentUser.displayName || 'You';
    } else if (manualAssignee === 'friend') {
      assignedUserId = partnerId;
      assignedUserName = partnerName;
    }

    try {
      await saveWordToSession(
        sessionId,
        {
          word: manualWordInput.trim(),
          type: manualType,
          assignedTo: manualAssignee,
          assignedUserId,
          assignedUserName,
          addedByUserId: currentUser.uid,
          addedByUserName: currentUser.displayName || 'Friend',
        },
        {
          hostId: session.hostId,
          hostName: session.hostName,
          friendId: session.friendId,
          friendName: session.friendName,
        }
      );
      setManualWordInput('');
      setShowManualModal(false);
    } catch (err) {
      console.error('Error adding manual word:', err);
    } finally {
      setIsAddingManual(false);
    }
  };

  const handleRemoveWord = async (wordKey: string) => {
    if (!sessionId) return;
    await removeWordFromSession(sessionId, wordKey);
  };

  const handleLaunchGame = async (gameType: SessionGameType, source: SessionGameContentSource) => {
    if (!sessionId || !currentUser || !session) return;
    try {
      await startSessionGame(
        sessionId,
        gameType,
        source,
        session.studyMaterial || null,
        savedList,
        { id: session.hostId, name: session.hostName, photo: session.hostPhoto },
        { id: session.friendId, name: session.friendName, photo: session.friendPhoto }
      );
    } catch (err) {
      console.error('Error starting session game:', err);
    }
  };

  const paragraphs = (session.studyMaterial?.content || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  const isInitialSession =
    !session.studyMaterial ||
    session.studyMaterial.type === 'none' ||
    session.studyMaterial.title === 'Welcome to Study Session' ||
    paragraphs.length === 0;

  return (
    <>
      <NavbarLanding isLanding={false} />
      <Navigation />

      <div className="min-h-screen bg-gray-50/60 md:ml-64 pb-16 md:pb-8">
        {/* Top Room Banner / Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-xs px-4 py-3 md:px-8">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
            {/* Left: Session Title & Partner Presence */}
            <div className="flex items-center gap-3 w-full md:w-auto">
              <button
                onClick={() => navigate('/social')}
                className="p-1.5 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
                title="Back to Social"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-2.5">
                <div className="relative">
                  {partnerPhoto ? (
                    <img
                      src={partnerPhoto}
                      alt={partnerName}
                      className="w-10 h-10 rounded-full object-cover border-2 border-purple-200"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 text-white font-bold flex items-center justify-center text-sm shadow-xs">
                      {partnerName?.charAt(0).toUpperCase() || 'P'}
                    </div>
                  )}
                  <span
                    className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                      isPartnerOnline ? 'bg-emerald-500' : 'bg-gray-400'
                    }`}
                    title={isPartnerOnline ? 'Online' : 'Offline'}
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-bold text-gray-900 text-sm md:text-base leading-tight">
                      Session with {partnerName}
                    </h2>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        session.status === 'active'
                          ? 'bg-emerald-100 text-emerald-800'
                          : session.status === 'pending'
                          ? 'bg-amber-100 text-amber-800 animate-pulse'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {session.status === 'active'
                        ? 'Active Room'
                        : session.status === 'pending'
                        ? 'Waiting for Partner'
                        : 'Ended'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">
                    {isPartnerOnline ? 'Connected now' : 'Partner offline'} • {savedList.length} words saved
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 w-full md:w-auto justify-end flex-wrap">
              <button
                onClick={() => setIsGameSelectorOpen(true)}
                className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 via-indigo-600 to-pink-600 text-white font-bold text-xs flex items-center gap-1.5 hover:opacity-90 shadow-xs transition-opacity"
              >
                <Gamepad2 className="w-3.5 h-3.5" />
                <span>Play Games</span>
              </button>

              <button
                onClick={() => setIsSelectorOpen(true)}
                className="px-3 py-1.5 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 font-semibold text-xs flex items-center gap-1.5 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                <span>Change Material</span>
              </button>

              <button
                onClick={() => setIsTrainingOpen(true)}
                disabled={savedList.length === 0}
                className="px-3.5 py-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold text-xs flex items-center gap-1.5 hover:opacity-95 shadow-xs transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Play className="w-3.5 h-3.5 fill-current" />
                <span>Train Words ({savedList.length})</span>
              </button>

              <button
                onClick={handleCopyLink}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
                title="Copy Session Link"
              >
                {copiedLink ? <Check className="w-4 h-4 text-emerald-600" /> : <Share2 className="w-4 h-4" />}
              </button>

              {isHost && (!isPartnerOnline || session.status === 'pending') && (
                <button
                  onClick={handlePingPartner}
                  disabled={isPingingPartner || pingSent}
                  className="px-2.5 py-1.5 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 font-semibold text-xs flex items-center gap-1.5 transition-colors disabled:opacity-60"
                  title="Send push notification to friend to join"
                >
                  <Bell className="w-3.5 h-3.5 text-purple-600" />
                  <span>{pingSent ? 'Notification Sent!' : 'Ping to Join'}</span>
                </button>
              )}

              {session.status !== 'ended' && (
                <button
                  type="button"
                  onClick={handleEndSession}
                  className="px-3 py-1.5 rounded-xl border border-red-200 text-red-600 bg-red-50/50 hover:bg-red-50 font-semibold text-xs flex items-center gap-1.5 transition-colors shadow-2xs"
                  title="Finish Study Session"
                >
                  <LogOut className="w-3.5 h-3.5 text-red-600" />
                  <span>Finish Session</span>
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Pending Invitation Banner for Invitee */}
        {!isHost && session.status === 'pending' && (
          <div className="bg-amber-500 text-white px-4 py-3 shadow-md flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Sparkles className="w-5 h-5 flex-shrink-0" />
              <span>{session.hostName} invited you to this Learn Session! Accept to start studying together.</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDeclineInvite}
                className="px-4 py-1.5 rounded-lg bg-amber-700/60 hover:bg-amber-700 text-xs font-semibold"
              >
                Decline
              </button>
              <button
                onClick={handleAcceptInvite}
                className="px-4 py-1.5 rounded-lg bg-white text-amber-900 hover:bg-amber-50 text-xs font-bold shadow-xs"
              >
                Accept & Join Room
              </button>
            </div>
          </div>
        )}

        {/* Mobile Tab Switcher */}
        <div className="md:hidden flex border-b border-gray-200 bg-white">
          <button
            onClick={() => setActiveMobileTab('reader')}
            className={`flex-1 py-2.5 text-xs font-bold flex items-center justify-center gap-1.5 border-b-2 ${
              activeMobileTab === 'reader'
                ? 'border-purple-600 text-purple-600 bg-purple-50/30'
                : 'border-transparent text-gray-500'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Study Text
          </button>
          <button
            onClick={() => setActiveMobileTab('words')}
            className={`flex-1 py-2.5 text-xs font-bold flex items-center justify-center gap-1.5 border-b-2 ${
              activeMobileTab === 'words'
                ? 'border-purple-600 text-purple-600 bg-purple-50/30'
                : 'border-transparent text-gray-500'
            }`}
          >
            <Bookmark className="w-4 h-4" />
            Saved Words ({savedList.length})
          </button>
        </div>

        {/* Main Room Layout */}
        <main className="max-w-7xl mx-auto p-4 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* LEFT COLUMN: Study Material & Reader (Col 1 to 7/8) */}
          <section
            className={`md:col-span-7 lg:col-span-8 space-y-4 ${
              activeMobileTab === 'reader' ? 'block' : 'hidden md:block'
            }`}
          >
            {isInitialSession ? (
              <div className="space-y-6">
                {/* 1. Past Sessions Vocabulary Card */}
                <div className="bg-gradient-to-br from-purple-50/80 via-indigo-50/40 to-white rounded-2xl border border-purple-100 p-5 md:p-6 shadow-xs">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-purple-100/70">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-purple-600 text-white rounded-xl shadow-xs shrink-0">
                        <Bookmark className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-900 text-base">Past Sessions Vocabulary</h3>
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                            {cumulativeWords.length} words
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Saved with {partnerName.split(' ')[0]} across all study sessions (sorted by most recent)
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => {
                          setShowHistory(true);
                          setActiveMobileTab('words');
                        }}
                        className="px-3 py-1.5 bg-white hover:bg-purple-50 text-purple-700 border border-purple-200 rounded-xl text-xs font-semibold shadow-2xs flex items-center gap-1.5 transition-colors"
                      >
                        <History className="w-3.5 h-3.5 text-purple-600" />
                        <span>Review All Words</span>
                      </button>
                      {cumulativeWords.length > 0 && (
                        <button
                          onClick={() => {
                            setShowHistory(true);
                            setIsTrainingOpen(true);
                          }}
                          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-semibold shadow-xs flex items-center gap-1.5 transition-colors"
                        >
                          <Play className="w-3 h-3 fill-current" />
                          <span>Train Past Words</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {cumulativeWords.length > 0 ? (
                    <div className="mt-3.5">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-purple-900/70 mb-2">
                        Recently Saved Words Preview:
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                        {cumulativeWords.slice(0, 3).map((w) => (
                          <div
                            key={w.id || w.word}
                            className="p-2.5 bg-white/90 rounded-xl border border-purple-100 shadow-2xs flex items-center justify-between gap-2"
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="font-bold text-gray-900 text-xs truncate">{w.word}</span>
                                <span className="text-[9px] uppercase px-1 py-0.2 rounded bg-purple-50 text-purple-700 font-semibold">
                                  {w.type}
                                </span>
                              </div>
                              {w.translation && (
                                <p className="text-[11px] text-purple-700 truncate font-medium">{w.translation}</p>
                              )}
                            </div>
                            <button
                              onClick={() => speak(w.word, 'en-US')}
                              className="p-1 text-gray-400 hover:text-purple-600 rounded transition-colors shrink-0"
                              title="Pronounce"
                            >
                              <Volume2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mt-3 italic">
                      No words saved in past sessions yet. As you and {partnerName.split(' ')[0]} read articles and click words, your shared vocabulary list will automatically grow!
                    </p>
                  )}
                </div>

                {/* 2. Start a New Activity */}
                <div className="bg-white rounded-2xl border border-gray-200/80 p-5 md:p-6 shadow-xs space-y-4">
                  <div>
                    <h2 className="text-base font-bold text-gray-900">Start a New Activity</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Choose an interactive activity to study together in real-time with {partnerName.split(' ')[0]}:
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    {/* Live News */}
                    <div
                      onClick={() => {
                        setMaterialModalTab('news');
                        setIsSelectorOpen(true);
                      }}
                      className="group relative p-4 rounded-2xl border border-gray-200/90 bg-gradient-to-br from-white to-purple-50/20 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center shadow-2xs group-hover:bg-purple-600 group-hover:text-white transition-colors">
                            <Newspaper className="w-5 h-5" />
                          </div>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                            Fresh Today
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-900 text-sm group-hover:text-purple-600 transition-colors">
                          Live News
                        </h3>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          Read today's world news across tech, science, world & economy. Click any word to translate, pronounce, and save to your shared vocabulary.
                        </p>
                      </div>
                      <div className="mt-4 pt-2.5 border-t border-gray-100 flex items-center text-xs font-semibold text-purple-600 group-hover:text-purple-700">
                        <span>Browse News Articles</span>
                        <ArrowRight className="w-3.5 h-3.5 ml-1 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>

                    {/* Mini-Games Duel */}
                    <div
                      onClick={() => setIsGameSelectorOpen(true)}
                      className="group relative p-4 rounded-2xl border border-gray-200/90 bg-gradient-to-br from-white to-pink-50/20 hover:border-pink-300 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center shadow-2xs group-hover:bg-pink-600 group-hover:text-white transition-colors">
                            <Gamepad2 className="w-5 h-5" />
                          </div>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 border border-pink-100">
                            Multiplayer
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-900 text-sm group-hover:text-pink-600 transition-colors">
                          Mini-Games Duel
                        </h3>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          Challenge each other in real-time in Pronunciation Duel, Word Tennis, Quick STOP & fun vocabulary mini-games.
                        </p>
                      </div>
                      <div className="mt-4 pt-2.5 border-t border-gray-100 flex items-center text-xs font-semibold text-pink-600 group-hover:text-pink-700">
                        <span>Play Mini-Games</span>
                        <ArrowRight className="w-3.5 h-3.5 ml-1 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>

                    {/* Curated Themes */}
                    <div
                      onClick={() => {
                        setMaterialModalTab('theme');
                        setIsSelectorOpen(true);
                      }}
                      className="group relative p-4 rounded-2xl border border-gray-200/90 bg-gradient-to-br from-white to-blue-50/20 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shadow-2xs group-hover:bg-blue-600 group-hover:text-white transition-colors">
                            <BookOpen className="w-5 h-5" />
                          </div>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                            Vocabulary Packs
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-900 text-sm group-hover:text-blue-600 transition-colors">
                          Curated Themes
                        </h3>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          Master essential vocabulary organized by real-life situations: Travel, Business, Food, Coffee Shop & Daily Life.
                        </p>
                      </div>
                      <div className="mt-4 pt-2.5 border-t border-gray-100 flex items-center text-xs font-semibold text-blue-600 group-hover:text-blue-700">
                        <span>Explore Themes</span>
                        <ArrowRight className="w-3.5 h-3.5 ml-1 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>

                    {/* Custom Text */}
                    <div
                      onClick={() => {
                        setMaterialModalTab('custom');
                        setIsSelectorOpen(true);
                      }}
                      className="group relative p-4 rounded-2xl border border-gray-200/90 bg-gradient-to-br from-white to-emerald-50/20 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shadow-2xs group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                            <PenTool className="w-5 h-5" />
                          </div>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                            Paste & Study
                          </span>
                        </div>
                        <h3 className="font-bold text-gray-900 text-sm group-hover:text-emerald-600 transition-colors">
                          Custom Text
                        </h3>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          Paste your own story, article, song lyrics, dialogue, or study passage to analyze and learn expressions together.
                        </p>
                      </div>
                      <div className="mt-4 pt-2.5 border-t border-gray-100 flex items-center text-xs font-semibold text-emerald-600 group-hover:text-emerald-700">
                        <span>Paste Custom Text</span>
                        <ArrowRight className="w-3.5 h-3.5 ml-1 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-5 md:p-8">
                {/* Material Title & Header */}
                <div className="border-b border-gray-100 pb-4 mb-6">
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-purple-600 bg-purple-50 px-2.5 py-1 rounded-full border border-purple-100">
                      {session.studyMaterial?.type === 'news'
                        ? 'Live News'
                        : session.studyMaterial?.type === 'theme'
                        ? 'Curated Theme'
                        : 'Study Article'}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => speak(session.studyMaterial?.content || '', 'en-US')}
                        className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 font-medium transition-colors"
                        title="Listen to full text"
                      >
                        <Volume2 className="w-3.5 h-3.5 text-purple-600" /> Listen
                      </button>
                    </div>
                  </div>

                  <h1 className="text-xl md:text-2xl font-bold text-gray-900 leading-snug">
                    {session.studyMaterial?.title || 'Study Article'}
                  </h1>
                  <p className="text-xs text-gray-400 mt-1">
                    💡 <span className="font-medium text-gray-600">Tip:</span> Click any word, or highlight multiple words to study expressions, then assign to you or your friend!
                  </p>
                </div>

                {/* Material Paragraphs */}
                <div className="space-y-4 font-sans text-gray-800 text-base md:text-lg leading-relaxed select-text">
                  {paragraphs.map((paragraph, idx) => (
                    <p key={idx} className="leading-relaxed">
                      <ClickableText
                        text={paragraph}
                        sessionId={session.id}
                        sessionPartner={{ id: partnerId, name: partnerName }}
                        enableExpressionSelection={true}
                        onSaveToSession={handleSaveWordFromPopover}
                      />
                    </p>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* RIGHT COLUMN: Session Saved Words Hub (Col 8/9 to 12) */}
          <section
            className={`md:col-span-5 lg:col-span-4 space-y-4 ${
              activeMobileTab === 'words' ? 'block' : 'hidden md:block'
            }`}
          >
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-4 md:p-5 flex flex-col h-full">
              {/* Hub Header */}
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-3">
                <div className="flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-purple-600" />
                  <h3 className="font-bold text-gray-900 text-sm">
                    {showHistory ? 'All Saved with Friend' : 'Session Saved Words'}
                  </h3>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className={`p-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors ${
                      showHistory
                        ? 'bg-purple-100 text-purple-800'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                    title={showHistory ? 'Switch to current session only' : 'View all previous words with this friend'}
                  >
                    <History className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => setShowManualModal(true)}
                    className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors"
                    title="Add Word / Expression Manually"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Filter Pills */}
              <div className="flex items-center gap-1 pb-3 overflow-x-auto text-[11px]">
                <button
                  onClick={() => setWordFilter('all')}
                  className={`px-2 py-0.5 rounded-md font-semibold transition-colors ${
                    wordFilter === 'all'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  All ({displayItems.length})
                </button>
                <button
                  onClick={() => setWordFilter('me')}
                  className={`px-2 py-0.5 rounded-md font-semibold transition-colors ${
                    wordFilter === 'me'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  For Me
                </button>
                <button
                  onClick={() => setWordFilter('friend')}
                  className={`px-2 py-0.5 rounded-md font-semibold transition-colors truncate max-w-[80px] ${
                    wordFilter === 'friend'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title={`For ${partnerName}`}
                >
                  For {partnerName.split(' ')[0]}
                </button>
                <button
                  onClick={() => setWordFilter('both')}
                  className={`px-2 py-0.5 rounded-md font-semibold transition-colors ${
                    wordFilter === 'both'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Both
                </button>
              </div>

              {/* List of Words */}
              <div className="flex-1 overflow-y-auto space-y-2.5 max-h-[550px] pr-1">
                {filteredItems.length === 0 ? (
                  <div className="text-center py-10 bg-gray-50/70 rounded-xl p-4 border border-dashed border-gray-200">
                    <Bookmark className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-600">No words found</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Click any word or highlight an expression in the article to add it here.
                    </p>
                  </div>
                ) : (
                  filteredItems.map((item) => (
                    <div
                      key={item.id || item.word}
                      className="p-3 bg-white rounded-xl border border-gray-100 hover:border-purple-200 shadow-xs transition-all group"
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-gray-900 text-sm">{item.word}</span>
                          <span
                            className={`text-[10px] font-semibold px-1.5 py-0.2 rounded ${
                              item.type === 'expression'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}
                          >
                            {item.type}
                          </span>
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => speak(item.word, 'en-US')}
                            className="p-1 text-gray-400 hover:text-purple-600 rounded transition-colors"
                            title="Pronounce"
                          >
                            <Volume2 className="w-3.5 h-3.5" />
                          </button>
                          {!showHistory && (
                            <button
                              onClick={() => handleRemoveWord(item.id || item.word)}
                              className="p-1 text-gray-300 hover:text-red-500 rounded transition-colors"
                              title="Remove from session"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      {item.translation && (
                        <p className="text-xs text-purple-700 font-medium mb-1">{item.translation}</p>
                      )}

                      {item.definition && (
                        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                          {item.definition}
                        </p>
                      )}

                      <div className="mt-2 pt-1.5 border-t border-gray-50 flex items-center justify-between text-[10px] text-gray-400">
                        <span className="font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                          {item.assignedTo === 'both'
                            ? '👥 For Both'
                            : item.assignedUserId === currentUser?.uid
                            ? '👤 For You'
                            : `👤 For ${partnerName.split(' ')[0]}`}
                        </span>
                        <span>Added by {item.addedByUserName?.split(' ')[0] || 'Friend'}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Bottom Actions */}
              <div className="pt-3 border-t border-gray-100 mt-auto space-y-2">
                {displayItems.length > 0 && (
                  <button
                    onClick={() => setIsTrainingOpen(true)}
                    className="w-full py-2.5 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold text-xs shadow-sm flex items-center justify-center gap-2 transition-colors"
                  >
                    <Play className="w-3.5 h-3.5 fill-current" />
                    <span>Train Session Words ({displayItems.length})</span>
                  </button>
                )}

                {session.status !== 'ended' && (
                  <button
                    type="button"
                    onClick={handleEndSession}
                    className="w-full py-2 px-3 border border-red-200 text-red-600 bg-red-50/40 hover:bg-red-50 rounded-xl font-semibold text-xs flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <LogOut className="w-3.5 h-3.5 text-red-500" />
                    <span>Finish & Review Session</span>
                  </button>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>

      {/* Material Selector Modal */}
      <MaterialSelectorModal
        isOpen={isSelectorOpen}
        onClose={() => setIsSelectorOpen(false)}
        initialTab={materialModalTab}
        onSelectMaterial={async (mat) => {
          if (!sessionId || !currentUser) return;
          await updateSessionStudyMaterial(sessionId, {
            ...mat,
            updatedBy: currentUser.uid,
            updatedByName: currentUser.displayName || 'Friend',
          });
        }}
      />

      {/* Session Training Modal */}
      <SessionTrainingModal
        isOpen={isTrainingOpen}
        onClose={() => setIsTrainingOpen(false)}
        savedItems={displayItems}
        hostId={session.hostId}
        friendId={session.friendId}
        hostName={session.hostName}
        friendName={session.friendName}
      />

      {/* Game Selection Modal */}
      <GameSelectionModal
        isOpen={isGameSelectorOpen}
        onClose={() => setIsGameSelectorOpen(false)}
        onLaunchGame={handleLaunchGame}
        materialTitle={session.studyMaterial?.title}
        savedWordsCount={savedList.length}
        hasMaterial={!!session.studyMaterial && session.studyMaterial.type !== 'none'}
        partnerName={partnerName}
      />

      {/* Finish Session Review & End Modal */}
      <FinishSessionModal
        isOpen={showFinishModal}
        onClose={() => setShowFinishModal(false)}
        onConfirmEnd={handleConfirmEndSession}
        savedItems={savedList}
        materialTitle={session.studyMaterial?.title}
        partnerName={partnerName}
      />

      {/* Active Game Arena */}
      {session.activeGame && (
        <SessionGameArena
          sessionId={session.id}
          game={session.activeGame}
          material={session.studyMaterial || null}
          savedItems={savedList}
          hostId={session.hostId}
          friendId={session.friendId}
          hostName={session.hostName}
          friendName={session.friendName}
          onClose={() => {}}
        />
      )}

      {/* Quick Manual Add Modal */}
      {showManualModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md p-5 space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
              <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                <Plus className="w-4 h-4 text-purple-600" />
                Add Word or Expression
              </h3>
              <button
                onClick={() => setShowManualModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                Word or Expression
              </label>
              <input
                type="text"
                value={manualWordInput}
                onChange={(e) => setManualWordInput(e.target.value)}
                placeholder="e.g., breakthrough, once in a blue moon..."
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Type</label>
                <select
                  value={manualType}
                  onChange={(e) => setManualType(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs bg-white"
                >
                  <option value="word">Single Word</option>
                  <option value="expression">Expression / Idiom</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Assign To</label>
                <select
                  value={manualAssignee}
                  onChange={(e) => setManualAssignee(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs bg-white"
                >
                  <option value="both">Both of Us</option>
                  <option value="me">Me Only</option>
                  <option value="friend">{partnerName}</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowManualModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddManualWord}
                disabled={!manualWordInput.trim() || isAddingManual}
                className="px-5 py-2 bg-purple-600 text-white rounded-xl text-xs font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {isAddingManual ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                <span>Save to Session</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
